import type { LandingDonor } from '@/lib/landing-auth'
import { useCallback, useState } from 'react'
import { getDonorDisplayName } from './landing-profile-format'
import { ProfileAvatar } from '@/features/donorAvatar/ProfileAvatar'
import type { AvatarConfig } from '@/features/donorAvatar/avatar-catalog'
import { ProfileMascot } from '@/features/donorMascot/ProfileMascot'
import type { MascotConfig } from '@/features/donorMascot/mascot-catalog'
import type { LandingAchievementId } from '@/lib/landing-achievements'
import { ACHIEVEMENT_DEFINITIONS } from '@/lib/landing-achievement-definitions'
import styles from './LandingNumbersPage.module.css'

type LandingProfileIdentityProps = {
  donor: LandingDonor
  coverUrl: string | null
  completionRate: number
  numbersCount: number
  purchasesCount: number
  earnedBadgeIds: LandingAchievementId[]
  onAvatarSaved: (avatar: AvatarConfig) => void
  onMascotSaved: (mascot: MascotConfig) => void
  onMascotGameClosed: () => void
  onVeterinaryPaid?: () => void
  campaignId: number
  avatarOpenSignal: number
  avatarBadgeUnlocked: boolean
  avatarBadgeEnabled?: boolean
  isProfileComplete: boolean
  onAvatarBadgeClick: () => void
  onCompleteProfile: () => void
  onViewNumbers: () => void
  onViewAchievements: () => void
}

export function LandingProfileIdentity({
  donor,
  coverUrl,
  completionRate,
  numbersCount,
  purchasesCount,
  earnedBadgeIds,
  onAvatarSaved,
  onMascotSaved,
  onMascotGameClosed,
  onVeterinaryPaid,
  campaignId,
  avatarOpenSignal,
  avatarBadgeUnlocked,
  avatarBadgeEnabled = false,
  isProfileComplete,
  onAvatarBadgeClick,
  onCompleteProfile,
  onViewNumbers,
  onViewAchievements,
}: LandingProfileIdentityProps) {
  const [resolvedAvatar, setResolvedAvatar] = useState<{
    donorId: number
    campaignId: number
    avatar: AvatarConfig | null
  } | null>(null)
  const rememberAvatar = useCallback(
    (avatar: AvatarConfig | null) => {
      setResolvedAvatar({ donorId: donor.id, campaignId, avatar })
    },
    [donor.id, campaignId]
  )
  const gameAvatar =
    resolvedAvatar?.donorId === donor.id && resolvedAvatar.campaignId === campaignId
      ? resolvedAvatar.avatar
      : donor.avatar
  const displayName = getDonorDisplayName(donor)
  const earnedBadges = ACHIEVEMENT_DEFINITIONS.filter((badge) =>
    earnedBadgeIds.includes(badge.id)
  )

  return (
    <section className={styles.identity} aria-label="Seu perfil">
      {coverUrl ? (
        <div className={styles.identityBackdrop} aria-hidden="true">
          <img src={coverUrl} alt="" decoding="async" />
        </div>
      ) : null}
      <div className={styles.identityTop}>
        <div className={styles.identityCompanions}>
          <ProfileAvatar
            key={donor.id}
            avatar={donor.avatar}
            photoUrl={donor.profile_photo_url}
            name={displayName}
            onResolved={rememberAvatar}
            onSaved={(avatar) => {
              rememberAvatar(avatar)
              onAvatarSaved(avatar)
            }}
            onCompleteProfile={onCompleteProfile}
            campaignId={campaignId}
            openSignal={avatarOpenSignal}
            isProfileComplete={isProfileComplete}
          />
          <ProfileMascot
            key={`mascot-${donor.id}`}
            campaignId={campaignId}
            mascot={donor.mascot}
            avatar={gameAvatar}
            isProfileComplete={isProfileComplete && !donor.is_guest}
            onCompleteProfile={onCompleteProfile}
            onSaved={onMascotSaved}
            onGameClosed={onMascotGameClosed}
            onVeterinaryPaid={onVeterinaryPaid}
          />
        </div>
        <div className={styles.identityCopy}>
          <div className={styles.identityNameRow}>
            <h1 className={styles.identityName} title={displayName}>
              {displayName}
            </h1>
            {avatarBadgeEnabled && (
              <button
                type="button"
                className={`${styles.identityVerifiedButton} ${avatarBadgeUnlocked ? '' : styles.identityVerifiedLocked}`}
                aria-label={
                  avatarBadgeUnlocked
                    ? 'Ver selo Avatar personalizado'
                    : isProfileComplete
                      ? 'Personalizar avatar para conquistar o selo'
                      : 'Complete seu perfil para liberar o avatar personalizado'
                }
                onClick={onAvatarBadgeClick}
              >
                <img
                  src="/conquistas%20doa%C3%A7%C3%B5es/17%20avatar%20personalizado.png"
                  alt=""
                  className={styles.identityVerifiedBadge}
                />
              </button>
            )}
          </div>
        </div>
      </div>

      <div className={styles.stats} aria-label="Sua participação">
        <button
          type="button"
          className={styles.stat}
          onClick={onViewNumbers}
          aria-label={`Ver meus números da sorte: ${numbersCount}`}
        >
          <span className={styles.statLabel}>Números da sorte</span>
          <strong className={styles.statValue}>{numbersCount}</strong>
        </button>
        <button
          type="button"
          className={styles.stat}
          onClick={onViewNumbers}
          aria-label={`Ver minhas compras: ${purchasesCount}`}
        >
          <span className={styles.statLabel}>Compras</span>
          <strong className={styles.statValue}>{purchasesCount}</strong>
        </button>
      </div>
      {earnedBadges.length > 0 ? (
        <div className={styles.earnedBadges} role="list" aria-label="Selos conquistados">
          {earnedBadges.map((badge) => (
            <span key={badge.id} role="listitem">
              <button
                type="button"
                className={styles.earnedBadgeButton}
                onClick={onViewAchievements}
                aria-label={`Ver selos conquistados: ${badge.title}`}
                title={badge.title}
              >
                <img src={badge.image} alt="" loading="lazy" />
              </button>
            </span>
          ))}
        </div>
      ) : null}
      {!isProfileComplete ? (
        <div className={styles.profileProgress}>
          <span
            className={styles.profileProgressTrack}
            role="progressbar"
            aria-label="Perfil preenchido"
            aria-valuenow={completionRate}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <span style={{ width: `${completionRate}%` }} />
          </span>
          <div className={styles.profileProgressRow}>
            <button
              type="button"
              className={styles.profileProgressMessage}
              onClick={onCompleteProfile}
              aria-label={`Perfil preenchido: ${completionRate}%. Ir para o cadastro`}
            >
              {completionRate === 100
                ? 'Salve seu cadastro para liberar o avatar personalizado.'
                : 'Complete e salve seu perfil para liberar o avatar personalizado e um novo selo.'}
            </button>
            <button
              type="button"
              className={styles.profileProgressAction}
              onClick={onCompleteProfile}
            >
              Completar cadastro
            </button>
          </div>
        </div>
      ) : null}
    </section>
  )
}
